/**
 * Impersonation support for admin viewing the app as a user.
 * Token is passed via hash fragment from admin panel.
 */

const STORAGE_KEY = "impersonation_token"

/**
 * Initialize impersonation from URL hash.
 * Should be called early in app initialization.
 * Reads token from hash, stores in sessionStorage, and reloads to ensure
 * Apollo client starts fresh with the impersonation token.
 */
export function initImpersonation(): void {
  const hash = window.location.hash
  if (!hash) return

  // Parse hash fragment: #impersonation=TOKEN
  const encoded = /^#impersonation=(.+)$/.exec(hash)?.[1]
  if (!encoded) return

  const token = decodeURIComponent(encoded)
  if (token) {
    sessionStorage.setItem(STORAGE_KEY, token)
    // Reload page without hash to ensure Apollo client starts with token
    // and no cached admin data
    window.location.replace(window.location.pathname + window.location.search)
  }
}

/**
 * Get current impersonation token.
 * @returns Token string or null if not impersonating
 */
export function getImpersonationToken(): string | null {
  return sessionStorage.getItem(STORAGE_KEY)
}

/**
 * Check if currently in impersonation mode.
 */
export function isImpersonating(): boolean {
  return !!getImpersonationToken()
}

/**
 * End impersonation session.
 * Clears token and reloads the page.
 */
export function endImpersonation(): void {
  sessionStorage.removeItem(STORAGE_KEY)
  window.location.reload()
}
